import { buildTitle } from "@/lib/seo-quality";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ArrowLeft, Flag, HelpCircle, Image as ImageIcon, Lock, MessageSquare, Pencil, Trash2 } from "lucide-react";
import { getForumPost, deleteForumPost, reportForumContent, updateForumPost } from "@/lib/forum.functions";
import { getThreadReadState, markThreadRead } from "@/lib/forum-reads.functions";
import { CommentThread, type ForumComment } from "@/components/forum/CommentThread";
import { ForumImages } from "@/components/forum/ForumImages";
import { VoteButtons } from "@/components/forum/VoteButtons";
import { ShareButtons } from "@/components/ShareButtons";
import { useAuth } from "@/hooks/use-auth";
import { listPublishedPosts } from "@/lib/blog-public.functions";
import { getTagsForPosts } from "@/lib/forum-extra.functions";
import { FollowButton } from "@/components/forum/FollowButton";
import { TagChips } from "@/components/forum/TagChips";
import { AuthorBadge } from "@/components/forum/AuthorBadge";
import { ForumBody } from "@/lib/forum-linkify";
import { FyndanuForumCard } from "@/components/forum/FyndanuForumCard";
import { AdSlot, StickyDesktopAd } from "@/components/AdSlot";


export const Route = createFileRoute("/forum/t/$slug")({
  loader: async ({ params }) => {
    const [res, blog] = await Promise.all([
      getForumPost({ data: { slug: params.slug } }),
      listPublishedPosts().catch(() => ({ posts: [] as any[] })),
    ]);
    if (!res.post) throw notFound();
    const words = new Set(
      `${res.post.title} ${res.post.body}`
        .toLowerCase()
        .replace(/[^a-zà-ÿ0-9åäö\s-]/g, " ")
        .split(/\s+/)
        .filter((w) => w.length >= 5),
    );
    const blogLinks = (blog?.posts ?? [])
      .map((p: any) => {
        const hay = `${p.title} ${p.focus_keyword ?? ""} ${(p.keywords ?? []).join(" ")}`.toLowerCase();
        const score = Array.from(words).reduce((acc, w) => (hay.includes(w) ? acc + 1 : acc), 0);
        return { slug: p.slug as string, title: p.title as string, score };
      })
      .filter((p: { score: number }) => p.score > 0)
      .sort((a: { score: number }, b: { score: number }) => b.score - a.score)
      .slice(0, 4)
      .map(({ slug, title }: { slug: string; title: string }) => ({ slug, title }));
    const tagRes = await getTagsForPosts({ data: { post_ids: [res.post.id] } }).catch(() => ({ byPost: {} }));
    const tags = (tagRes.byPost as Record<string, Array<{ slug: string; name: string }>>)[res.post.id] ?? [];
    return { ...res, blogLinks, tags };
  },

  head: ({ loaderData }) => {
    if (!loaderData?.post) {
      return { meta: [{ title: "Tråden hittades inte – Fritidsforum" }, { name: "robots", content: "noindex" }] };
    }
    const p = loaderData.post;
    const desc = p.body.replace(/\s+/g, " ").slice(0, 155);
    const url = `https://www.akhusbilfritid.se/forum/t/${encodeURIComponent(p.slug)}`;
    // Publika bilder (http) kan användas som delningsbild; signerade
    // lagringssökvägar går inte att nå för sociala crawlers → forumets standardbild.
    const publicImage = (p.image_urls ?? []).find((u: string) => u.startsWith("http"));
    const image = publicImage ?? "https://www.akhusbilfritid.se/forum-og.jpg";
    return {
      meta: [
        { title: buildTitle(p.title, "Fritidsforum") },
        { name: "description", content: desc },
        { property: "og:title", content: p.title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:image", content: image },
        { property: "og:image:alt", content: `${p.title} – Fritidsforum` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: image },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            p.post_type === "question"
              ? {
                  "@context": "https://schema.org",
                  "@type": "QAPage",
                  mainEntity: {
                    "@type": "Question",
                    name: p.title,
                    text: p.body.slice(0, 1200),
                    answerCount: loaderData.comments.length,
                    dateCreated: p.created_at,
                    author: { "@type": "Person", name: p.author.display_name || "Medlem" },
                    ...(() => {
                      const best = loaderData.comments.find((c: any) => c.is_best_answer);
                      return best
                        ? {
                            acceptedAnswer: {
                              "@type": "Answer",
                              text: best.body.slice(0, 1200),
                              upvoteCount: Math.max(0, best.vote_score),
                              dateCreated: best.created_at,
                              url: `${url}#comment-${best.id}`,
                              author: { "@type": "Person", name: best.author.display_name || "Medlem" },
                            },
                          }
                        : {};
                    })(),
                    ...(loaderData.comments.length
                      ? {
                          suggestedAnswer: loaderData.comments.slice(0, 5).map((c: any) => ({
                            "@type": "Answer",
                            text: c.body.slice(0, 800),
                            upvoteCount: Math.max(0, c.vote_score),
                            dateCreated: c.created_at,
                            author: { "@type": "Person", name: c.author.display_name || "Medlem" },
                          })),
                        }
                      : {}),
                  },
                }
              : {
                  "@context": "https://schema.org",
                  "@type": "DiscussionForumPosting",
                  headline: p.title,
                  text: p.body.slice(0, 1200),
                  datePublished: p.created_at,
                  dateModified: (p as any).last_activity_at ?? p.created_at,
                  url,
                  author: { "@type": "Person", name: p.author.display_name || "Medlem" },
                  interactionStatistic: [
                    {
                      "@type": "InteractionCounter",
                      interactionType: "https://schema.org/CommentAction",
                      userInteractionCount: loaderData.comments.length,
                    },
                    {
                      "@type": "InteractionCounter",
                      interactionType: "https://schema.org/LikeAction",
                      userInteractionCount: Math.max(0, p.vote_score),
                    },
                  ],
                },
          ),
        },
      ],
    };
  },
  errorComponent: () => <Fallback title="Något gick fel" />,
  notFoundComponent: () => <Fallback title="Tråden finns inte" />,
  component: ThreadPage,
});

function Fallback({ title }: { title: string }) {
  return (
    <section className="bg-warm">
      <div className="container-edge py-24 text-center">
        <h1 className="font-display text-4xl">{title}</h1>
        <Link to="/forum" className="mt-6 inline-flex items-center gap-2 text-accent hover:underline">
          <ArrowLeft size={15} /> Till forumet
        </Link>
      </div>
    </section>
  );
}

const TYPE_LABEL: Record<string, { label: string; icon: typeof MessageSquare }> = {
  discussion: { label: "Diskussion", icon: MessageSquare },
  question: { label: "Fråga", icon: HelpCircle },
  trip: { label: "Bild & resa", icon: ImageIcon },
};

function ThreadPage() {
  const initial = Route.useLoaderData();
  const { slug } = Route.useParams();
  const fn = useServerFn(getForumPost);
  const del = useServerFn(deleteForumPost);
  const report = useServerFn(reportForumContent);
  const { user, signedIn } = useAuth();
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ["forum", "thread", slug],
    queryFn: () => fn({ data: { slug } }),
    initialData: initial,
  });

  const post = data?.post ?? initial.post;
  const category = data?.category ?? initial.category;
  const comments = (data?.comments ?? []) as ForumComment[];
  const related = data?.related ?? [];
  const tags = ((initial as any).tags ?? []) as Array<{ slug: string; name: string }>;
  const blogLinks = (initial as any).blogLinks as Array<{ slug: string; title: string }> | undefined;

  const savePost = useServerFn(updateForumPost);
  const readState = useServerFn(getThreadReadState);
  const markRead = useServerFn(markThreadRead);
  const [lastReadAt, setLastReadAt] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(post?.title ?? "");
  const [draftBody, setDraftBody] = useState(post?.body ?? "");
  const [savingPost, setSavingPost] = useState(false);
  const markedRef = useRef(false);

  useEffect(() => {
    if (!signedIn || !post?.id || markedRef.current) return;
    markedRef.current = true;
    (async () => {
      try {
        const res = await readState({ data: { post_id: post.id } });
        setLastReadAt(res.lastReadAt ?? null);
        await markRead({ data: { post_id: post.id } });
      } catch {
        /* tyst */
      }
    })();
  }, [signedIn, post?.id, readState, markRead]);

  if (!post) return <Fallback title="Tråden finns inte" />;

  const meta = TYPE_LABEL[post.post_type] ?? TYPE_LABEL.discussion;
  const Icon = meta.icon;
  const isOwn = user?.id === post.user_id;

  async function saveThread() {
    if (draftTitle.trim().length < 6 || draftBody.trim().length < 10) return;
    setSavingPost(true);
    try {
      await savePost({ data: { id: post!.id, title: draftTitle.trim(), body: draftBody.trim() } });
      await qc.invalidateQueries({ queryKey: ["forum"] });
      setEditing(false);
      toast.success("Tråden uppdaterad");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Kunde inte spara");
    } finally {
      setSavingPost(false);
    }
  }



  async function remove() {
    if (!confirm("Ta bort hela tråden?")) return;
    try {
      await del({ data: { id: post!.id } });
      qc.invalidateQueries({ queryKey: ["forum"] });
      toast.success("Tråden borttagen");
      window.location.href = "/forum";
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Kunde inte ta bort");
    }
  }

  async function flag() {
    const reason = prompt("Varför rapporterar du tråden? (spam, hot, off-topic…)");
    if (!reason) return;
    try {
      await report({ data: { post_id: post!.id, reason } });
      toast.success("Tack — vi tittar på det");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Kunde inte rapportera");
    }
  }

  return (
    <section className="bg-warm">
      <div className="container-edge py-10 md:py-16">
        <nav className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <Link to="/forum" className="hover:text-foreground">Fritidsforum</Link>
          {category && (
            <>
              <span>/</span>
              <Link to="/forum/k/$slug" params={{ slug: category.slug }} className="hover:text-foreground">
                {category.name}
              </Link>
            </>
          )}
        </nav>

        <div className="mt-6 grid gap-10 lg:grid-cols-[minmax(0,1fr)_280px]">
          <div className="min-w-0">
            <article className="rounded-3xl border border-border bg-card p-5 sm:p-7">
              <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Icon size={12} /> {meta.label}
                </span>
                {post.locked && (
                  <span className="inline-flex items-center gap-1">
                    <Lock size={12} /> Låst
                  </span>
                )}
              </div>

              {editing ? (
                <input
                  value={draftTitle}
                  onChange={(e) => setDraftTitle(e.target.value)}
                  maxLength={160}
                  className="mt-3 w-full rounded-2xl border border-input bg-background px-4 py-3 font-display text-2xl outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
                />
              ) : (
                <h1 className="mt-3 font-display text-3xl leading-tight sm:text-4xl md:text-5xl">{post.title}</h1>
              )}

              <p className="mt-3 text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">{post.author.display_name || "Medlem"}</span>{" "}
                <AuthorBadge badge={post.author.badge} /> ·{" "}

                {new Date(post.created_at).toLocaleDateString("sv-SE")} · {post.comment_count} svar
                {(post as any).edited_at ? <span className="ml-1 italic">· redigerad</span> : null}
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-3">
                <TagChips tags={tags} size="md" />
                <FollowButton postId={post.id} label="Bevaka tråden" />
                {category && <FollowButton categoryId={category.id} label={`Bevaka ${category.name}`} />}
              </div>

              <div className="mt-6 flex items-start gap-4">
                <VoteButtons postId={post.id} score={post.vote_score} />
                <div className="min-w-0 flex-1">
                  {editing ? (
                    <div className="space-y-3">
                      <textarea
                        value={draftBody}
                        onChange={(e) => setDraftBody(e.target.value)}
                        rows={10}
                        maxLength={20000}
                        className="w-full rounded-2xl border border-input bg-background px-4 py-3 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
                      />
                      <div className="flex items-center gap-3">
                        <button
                          onClick={saveThread}
                          disabled={savingPost}
                          className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60"
                        >
                          {savingPost ? "Sparar…" : "Spara ändringar"}
                        </button>
                        <button
                          onClick={() => {
                            setEditing(false);
                            setDraftTitle(post!.title);
                            setDraftBody(post!.body);
                          }}
                          className="text-sm text-muted-foreground hover:text-foreground"
                        >
                          Avbryt
                        </button>
                      </div>
                    </div>
                  ) : (
                    <ForumBody
                      body={post.body}
                      className="whitespace-pre-wrap break-words text-base leading-relaxed text-foreground/90"
                    />
                  )}

                  <ForumImages paths={post.image_urls ?? []} className="mt-5" />
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-4 border-t border-border pt-5 text-xs text-muted-foreground">
                {isOwn && !editing && (
                  <button
                    onClick={() => {
                      setDraftTitle(post!.title);
                      setDraftBody(post!.body);
                      setEditing(true);
                    }}
                    className="inline-flex items-center gap-1 hover:text-foreground"
                  >
                    <Pencil size={13} /> Redigera
                  </button>
                )}
                {isOwn && (
                  <button onClick={remove} className="inline-flex items-center gap-1 hover:text-destructive">
                    <Trash2 size={13} /> Ta bort tråd
                  </button>
                )}
                {signedIn && !isOwn && (
                  <button onClick={flag} className="inline-flex items-center gap-1 hover:text-foreground">
                    <Flag size={13} /> Rapportera
                  </button>
                )}
                <Link to="/forum/regler" className="hover:text-foreground">
                  Forumregler
                </Link>
                <ShareButtons path={`/forum/t/${post.slug}`} title={post.title} />
              </div>
            </article>

            <AdSlot variant="in-feed" label="Annons" />

            <CommentThread
              postId={post.id}
              postAuthorId={post.user_id}
              locked={post.locked}
              comments={comments}
              isQuestion={post.post_type === "question"}
              lastReadAt={lastReadAt}
            />


          </div>

          <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
            {category && (
              <div className="rounded-2xl border border-border bg-card p-5">
                <p className="eyebrow">Kategori</p>
                <Link
                  to="/forum/k/$slug"
                  params={{ slug: category.slug }}
                  className="mt-2 block font-display text-xl hover:text-accent"
                >
                  {category.name}
                </Link>
                {category.description && (
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{category.description}</p>
                )}
              </div>
            )}

            {related.length > 0 && (
              <div className="rounded-2xl border border-border bg-card p-5">
                <p className="eyebrow">Mer i kategorin</p>
                <ul className="mt-3 space-y-3">
                  {related.map((r: { id: string; slug: string; title: string; comment_count: number }) => (
                    <li key={r.id}>
                      <Link
                        to="/forum/t/$slug"
                        params={{ slug: r.slug }}
                        className="text-sm font-medium leading-snug hover:text-accent"
                      >
                        {r.title}
                      </Link>
                      <p className="mt-0.5 text-xs text-muted-foreground">{r.comment_count} svar</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {blogLinks && blogLinks.length > 0 && (
              <div className="rounded-2xl border border-border bg-card p-5">
                <p className="eyebrow">Guider om detta</p>
                <ul className="mt-3 space-y-3">
                  {blogLinks.map((b) => (
                    <li key={b.slug}>
                      <Link
                        to="/blogg/$slug"
                        params={{ slug: b.slug }}
                        className="text-sm font-medium leading-snug hover:text-accent"
                      >
                        {b.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <FyndanuForumCard context={`${post.title} ${category?.name ?? ""} ${post.body.slice(0, 400)}`} />

            <StickyDesktopAd />


            <div className="rounded-2xl border border-border bg-card p-5">


              <p className="eyebrow">Behöver du hjälp på riktigt?</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Vår verkstad i Kalix fixar el, vatten, värme och gasol. Vi tar även fram reservdelar.
              </p>
              <Link
                to="/reservdelar"
                className="mt-3 inline-flex rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground"
              >
                Reservdelar & service
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
